<template>
  <el-dialog
    title="关联奖品"
    :visible="visible"
    :close-on-click-modal="false"
    :show-close="false"
    :modal-append-to-body="false"
    width="920px"
    @close="onClose"
  >
    <div class="prize-search-row">
      <span class="search-label">奖品名称</span>
      <el-input
        v-model="prizeSearchText"
        placeholder="模糊搜索"
        class="prize-search-input"
        @keyup.enter.native="onSearch"
      ></el-input>
      <el-button type="primary" size="small" style="margin-left:8px" @click="onSearch">搜索</el-button>
      <span class="prize-selected-tip">
        已选 <b>{{ selectedPrizeIds.length }}</b> / 10
        <span v-if="selectedPrizeIds.length >= 10" class="prize-limit-warn">（已达上限）</span>
      </span>
    </div>

    <el-table
      ref="prizeTable"
      v-loading="loading"
      :data="prizeList"
      border
      class="prize-table"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" width="50" align="center"></el-table-column>
      <el-table-column prop="prizeId" label="奖品ID" width="90" align="center"></el-table-column>
      <el-table-column prop="prizeName" label="奖品名称" width="160"></el-table-column>
      <el-table-column prop="prizeType" label="奖品类型" width="110" align="center">
        <template slot-scope="scope">
          {{ getPrizeTypeLabel(scope.row.prizeType) }}
        </template>
      </el-table-column>
      <el-table-column label="奖品详情" min-width="200" align="center">
        <template slot-scope="scope">
          <!-- 多图 -->
          <div v-if="scope.row.detailType === 'multiImage'" class="detail-multi-img">
            <img
              v-for="(img, idx) in scope.row.detail"
              :key="idx"
              :src="img"
              class="detail-img-small"
            />
          </div>
          <!-- 单图 -->
          <img
            v-else-if="scope.row.detailType === 'image'"
            :src="scope.row.detail"
            class="detail-img"
          />
          <!-- 文本 -->
          <span v-else class="detail-text">{{ scope.row.detail || '–' }}</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="prize-pagination-row">
      <el-pagination
        :current-page="pageNum"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      ></el-pagination>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="onClose">取消</el-button>
      <el-button type="primary" @click="onConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script>
const LOG_TAG = "[关联奖品]";
const PRIZE_LIST_URL = "/adminapi/competition/prize/v70310/list";

const PRIZE_TYPE_LABELS = {
  0: "站内会员",
  1: "头像挂件",
  2: "主题",
  3: "电子证书",
  4: "电子奖牌",
  5: "实物",
};

export default {
  name: "PrizeSelectDialog",

  props: {
    /** 控制弹窗显示，支持 .sync */
    visible: {
      type: Boolean,
      default: false,
    },
    /** 当前套餐已关联的奖品列表，用于初始化勾选状态 */
    existingPrizes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      prizeSearchText: "",
      prizeList: [],
      loading: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      /** 跨分页已选 ID 列表 */
      selectedPrizeIds: [],
      /** 跨分页已选完整行数据：{ [prizeId]: prizeRow }，供 confirm 时使用 */
      pendingPrizeMap: {},
      /** 恢复勾选期间屏蔽 onSelectionChange */
      _restoringSelection: false,
    };
  },

  watch: {
    visible(val) {
      if (val) this.init();
    },
  },

  methods: {
    /** 弹窗打开时初始化状态并加载第一页 */
    init() {
      this.prizeSearchText = "";
      this.pageNum = 1;
      this.total = 0;
      this.prizeList = [];
      const ids = [];
      const map = {};
      (this.existingPrizes || []).forEach(p => {
        ids.push(p.prizeId);
        map[p.prizeId] = p;
      });
      this.selectedPrizeIds = ids;
      this.pendingPrizeMap = map;
      this.loadData();
    },

    /** 将奖品列表接口单行映射为表格行 */
    normalizeRow(raw) {
      const detail = raw.detail != null ? String(raw.detail) : "";
      // 判断 detail 是图片 URL、JSON 图片数组还是纯文字
      let detailType = "text";
      let detailValue = detail || "–";
      if (detail) {
        try {
          const parsed = JSON.parse(detail);
          if (Array.isArray(parsed) && parsed.length > 0) {
            if (typeof parsed[0] === "string") {
              // 字符串数组：直接作为图片 URL 列表
              const urls = parsed.filter((v) => typeof v === "string" && v);
              if (urls.length > 0) { detailType = "multiImage"; detailValue = urls; }
            } else {
              // 对象数组（如主题）：从每个对象里提取图片 URL 值
              const urls = [];
              parsed.forEach((item) => {
                if (item && typeof item === "object") {
                  Object.values(item).forEach((v) => {
                    if (typeof v === "string" && this.isImageUrl(v)) urls.push(v);
                  });
                }
              });
              if (urls.length > 0) { detailType = "multiImage"; detailValue = urls; }
            }
          } else if (parsed && typeof parsed === "object") {
            // 单个对象（主题等）：值均为图片 URL
            const urls = Object.values(parsed).filter((v) => typeof v === "string" && this.isImageUrl(v));
            if (urls.length > 0) {
              detailType = "multiImage";
              detailValue = urls;
            }
          } else if (typeof parsed === "string") {
            detailType = this.isImageUrl(parsed) ? "image" : "text";
            detailValue = parsed || "–";
          }
        } catch (_) {
          detailType = this.isImageUrl(detail) ? "image" : "text";
        }
      }
      return {
        prizeId: raw.id,
        prizeName: raw.name || "",
        prizeType: raw.type,
        detail: detailValue,
        detailType,
      };
    },

    isImageUrl(str) {
      return /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(str);
    },

    getPrizeTypeLabel(type) {
      if (type == null || type === "") return "–";
      const n = Number(type);
      return Number.isFinite(n) && PRIZE_TYPE_LABELS[n] != null
        ? PRIZE_TYPE_LABELS[n]
        : String(type);
    },

    async loadData() {
      this.loading = true;
      try {
        const reqBody = { pageNum: this.pageNum, pageSize: this.pageSize, status: 1 };
        if (this.prizeSearchText && this.prizeSearchText.trim()) {
          reqBody.name = this.prizeSearchText.trim();
        }
        const res = await this.$http({
          url: this.$util.domain + PRIZE_LIST_URL,
          method: "POST",
          body: JSON.stringify(reqBody),
        });
        const body = res.body !== undefined ? res.body : res.data;
        if (body.error !== 10000) {
          console.warn(LOG_TAG, "list fail", body);
          this.$util.errorMsg(body.message || body.msg || "加载奖品列表失败");
          this.prizeList = [];
          this.total = 0;
          return;
        }
        const data = body.data || {};
        this.total = data.total || 0;
        // 赋值前先锁住，防止 el-table 内部因数据变化触发 selection-change
        this._restoringSelection = true;
        this.prizeList = (Array.isArray(data.list) ? data.list : []).map(row => this.normalizeRow(row));
        this.restoreChecked();
      } catch (err) {
        console.warn(LOG_TAG, "list error", err);
        this.$util.errorMsg("网络异常，请稍后重试");
        this.prizeList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    /** 根据 selectedPrizeIds 恢复当前页勾选，对每行明确传 true/false */
    restoreChecked() {
      this.$nextTick(() => {
        const table = this.$refs.prizeTable;
        if (!table) return;
        const selectedSet = new Set(this.selectedPrizeIds);
        this.prizeList.forEach(row => {
          table.toggleRowSelection(row, selectedSet.has(row.prizeId));
        });
        this.$nextTick(() => {
          this._restoringSelection = false;
        });
      });
    },

    onSelectionChange(val) {
      if (this._restoringSelection) return;
      const MAX = 10;
      const currentPageIds = new Set(this.prizeList.map(p => p.prizeId));
      const selectedOnPage = new Set(val.map(p => p.prizeId));
      // 保留其他页已选 ID，替换当前页的选中结果
      const kept = this.selectedPrizeIds.filter(id => !currentPageIds.has(id));
      selectedOnPage.forEach(id => kept.push(id));

      // 超出上限时，撤销本次新增的超出部分，并提示
      if (kept.length > MAX) {
        const otherPageCount = this.selectedPrizeIds.filter(id => !currentPageIds.has(id)).length;
        const allowOnPage = Math.max(0, MAX - otherPageCount);
        // 找出本次新勾选的（之前没有的）
        const prevOnPage = new Set(this.selectedPrizeIds.filter(id => currentPageIds.has(id)));
        const newlyAdded = [...selectedOnPage].filter(id => !prevOnPage.has(id));
        // 撤销超出部分
        const toRemove = newlyAdded.slice(allowOnPage - prevOnPage.size > 0 ? allowOnPage - prevOnPage.size : 0);
        if (toRemove.length > 0) {
          this.$message.warning(`最多关联 ${MAX} 个奖品`);
          this._restoringSelection = true;
          toRemove.forEach(id => {
            const row = this.prizeList.find(p => p.prizeId === id);
            if (row) this.$refs.prizeTable && this.$refs.prizeTable.toggleRowSelection(row, false);
          });
          this.$nextTick(() => { this._restoringSelection = false; });
          return;
        }
      }

      this.selectedPrizeIds = kept;
      // 同步更新完整行数据 map
      currentPageIds.forEach(id => {
        if (!selectedOnPage.has(id)) {
          this.$delete(this.pendingPrizeMap, id);
        }
      });
      val.forEach(row => {
        this.$set(this.pendingPrizeMap, row.prizeId, row);
      });
    },

    onSearch() {
      this.pageNum = 1;
      this.loadData();
    },

    onPageChange(page) {
      this.pageNum = page;
      this.loadData();
    },

    onSizeChange(size) {
      this.pageSize = size;
      this.pageNum = 1;
      this.loadData();
    },

    onConfirm() {
      this.$emit("confirm", { pendingMap: { ...this.pendingPrizeMap } });
      this.$emit("update:visible", false);
    },

    onClose() {
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
.prize-search-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.prize-selected-tip {
  margin-left: 16px;
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}
.prize-limit-warn {
  color: #f56c6c;
}
.search-label {
  font-size: 14px;
  color: #606266;
  margin-right: 8px;
  white-space: nowrap;
}
.prize-search-input {
  width: 220px;
}
.prize-table {
  width: 100%;
}
.prize-pagination-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

/* 奖品详情列 */
.detail-text {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
  word-break: break-all;
}
.detail-img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}
.detail-multi-img {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}
.detail-img-small {
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}
</style>



// WEBPACK FOOTER //
// PrizeSelectDialog.vue?497b2770